// src/v2/components/StatusPill.jsx
//
// Canonical pill component: squarish border-radius (6), borderless,
// tinted fill, semibold weight. Used everywhere a status / category /
// label pill appears — drawers, lists, plan, signals, imports, etc.
//
// Props:
//   label     string?  — uppercase caption rendered above the pill (column
//                        layout). Omit for a standalone pill with no caption.
//   value     string   — text shown inside the pill (falls back to "—")
//   bg        string?  — pill background; defaults to var(--v2-tint)
//   fg        string?  — pill text color; defaults to var(--v2-mid)
//   testId    string?  — data-testid on the outermost element
//   size      "sm"|"md"  — "sm" uses tighter padding (2px 7px); default "md"
//   centered  boolean  — justify-content: center on the wrapper div
//   style     object?  — extra styles merged onto the pill span (e.g. minWidth)
//
// CANONICAL PILL SPEC (md) — matches PlanPill in MarketPlanPage, the height
// reference:
//   height: 22px  |  padding: 0 10px  |  lineHeight: 1  |  borderRadius: 6
//   fontSize: T.fsXs  |  fontWeight: T.fwSemibold  |  border: none

import React from "react";
import { T } from "../../app/tokens.js";

export function StatusPill({ label, value, bg, fg, testId, size = "md", centered = false, style: extraStyle }) {
  const isMd     = size !== "sm";
  const pillPad  = isMd ? "0 10px" : "2px 7px";

  const pillSpan = (
    <span
      style={{
        display:         "inline-flex",
        alignItems:      "center",
        justifyContent:  "center",
        borderRadius:    6,
        height:          isMd ? 22 : undefined,
        lineHeight:      isMd ? 1  : undefined,
        padding:         pillPad,
        fontSize:        T.fsXs,
        fontWeight:      T.fwSemibold,
        color:           fg  || "var(--v2-mid)",
        background:      bg  || "var(--v2-tint)",
        whiteSpace:      "nowrap",
        ...extraStyle,
      }}
    >
      {value || "—"}
    </span>
  );

  // Standalone mode — no caption label above the pill.
  if (!label) {
    return <span data-testid={testId}>{pillSpan}</span>;
  }

  // Column mode — uppercase caption above + pill below.
  return (
    <div
      data-testid={testId}
      style={{
        display:        "flex",
        flexDirection:  "column",
        gap:            2,
        alignItems:     centered ? "center" : undefined,
      }}
    >
      <span
        style={{
          fontSize:      T.fsXxxs5,
          color:         T.light,
          fontWeight:    T.fwSemibold,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      {pillSpan}
    </div>
  );
}
